/**
 * THE PASTE SANITIZER (`061-editor.md` §4).
 *
 * **PURE: string in, string out.** No editor access, no React, no network, no DOM mutation
 * outside the detached document it parses. That is what makes the highest-risk code in the
 * project testable against a fixture corpus instead of by hand in a browser.
 *
 * Every behavioural change here must come with a fixture test in `./__fixtures__`
 * (`130-testing.md` §2.1). The corpus is the deliverable that makes this trustworthy — not
 * the code.
 *
 * Two independent gates protect the document, and this is only the FIRST:
 *
 *  1. this function — removes Word's noise so it cannot confuse ProseMirror's *structural*
 *     parsing, and removes images the browser cannot load;
 *  2. **ProseMirror's schema** — drops every node and mark the extension list does not
 *     define. That is the security boundary (`120-non-functional.md`), not this file. A
 *     `<script>` never executes because Tiptap renders from JSON through a schema that has
 *     no place to put one.
 *
 * The attribute policy is an **allowlist** (`ALLOWED_ATTRS`), which is why no `mso-` or
 * `Mso*` string is enumerated anywhere below: dropping every `style` and every `class`
 * kills the entire Word vocabulary without ever naming it. Enumerating Word's vocabulary is
 * a losing game; not enumerating anything else is a winning one.
 */

/** What a paste actually did, for the toast (`061` §4.4, §4.7). Derived, never guessed. */
export interface PasteReport {
  /** `file:///` and `cid:` — the bytes are not on the clipboard as HTML. Node removed. */
  droppedLocalImages: number;
  /** `https://` images kept as remote hotlinks. They may rot; say so. */
  remoteImages: number;
  tables: number;
  /** Top-level blocks that survived, for "Pasted 1 table, 12 paragraphs". */
  paragraphs: number;
}

/* ── The allowlist ──────────────────────────────────────────────────────────── */

const ALLOWED_ATTRS: Record<string, readonly string[]> = {
  TD: ['colspan', 'rowspan'],
  TH: ['colspan', 'rowspan'],
  A: ['href'],
  IMG: ['src', 'alt', 'width', 'height'],
  OL: ['start'],
  // GitHub-flavoured task lists paste as `<li><input type=checkbox checked>`. `convertTaskLists`
  // rewrites them into the shape TaskList/TaskItem actually parse (`ul[data-type=taskList]`,
  // `li[data-type=taskItem][data-checked]`), so those two attrs — and only those two — survive.
  UL: ['data-type'],
  LI: ['data-type', 'data-checked'],
};

/** Office namespaces. DOMParser gives `<o:p>` a `nodeName` of `O:P` — match nodeName. */
const OFFICE_NS = /^(?:O|W|V|M|X|ST1):/i;

/**
 * Nothing inside these was ever *visible*, so there is nothing to salvage: the subtree goes.
 * A `<script>`'s contents must never appear in the document as text (`130-testing.md` §2.1
 * #12), which is precisely what "salvage the text" would do.
 */
const HARD_KILL: ReadonlySet<string> = new Set([
  'STYLE',
  'META',
  'LINK',
  'SCRIPT',
  'XML',
  'TITLE',
  'NOSCRIPT',
]);

/**
 * Embedded objects. The node has no schema mapping and is dropped — but if it carried
 * *visible* fallback text, that text becomes a paragraph rather than vanishing. The
 * graceful-degradation contract does not make an exception for an `<iframe>`.
 */
const SALVAGE_KILL: ReadonlySet<string> = new Set([
  'IFRAME',
  'VIDEO',
  'AUDIO',
  'OBJECT',
  'EMBED',
  'SVG',
  'CANVAS',
  'BUTTON',
  'SELECT',
  'TEXTAREA',
]);

/**
 * Unwrapped: the element goes, the text stays. `031` has no mark for any of them, so the
 * text survives as plain text in its parent — the graceful-degradation contract (§4.5).
 */
const UNWRAP_TAGS = 'font,sup,sub,mark,abbr,kbd,small,big,center,ins,cite,dfn,var,samp,time,ruby';

/** Block-ish containers with no semantic mapping. Degrade to `<p>`, or unwrap (§4.5). */
const SOUP_TAGS =
  'div,section,article,aside,header,footer,main,nav,figure,figcaption,form,fieldset,details,summary,dl,dt,dd,address';

/** Anything that must not end up inside a `<p>` when we degrade a soup element. */
const BLOCK_TAGS: ReadonlySet<string> = new Set([
  'P',
  'DIV',
  'SECTION',
  'ARTICLE',
  'ASIDE',
  'HEADER',
  'FOOTER',
  'MAIN',
  'NAV',
  'FIGURE',
  'FIGCAPTION',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'UL',
  'OL',
  'LI',
  'TABLE',
  'THEAD',
  'TBODY',
  'TR',
  'TD',
  'TH',
  'BLOCKQUOTE',
  'PRE',
  'HR',
  'FORM',
  'DL',
  'DT',
  'DD',
  'ADDRESS',
  'DETAILS',
  'SUMMARY',
]);

/* ── Public API ─────────────────────────────────────────────────────────────── */

/** `editorProps.transformPastedHTML`. Runs before ProseMirror parses anything. */
export function transformPastedHTML(html: string): string {
  if (!html) return html;
  return sanitize(html).html;
}

/** The same pass, for the paste toast. Never mutates and never touches the editor. */
export function inspectPastedHTML(html: string): PasteReport {
  if (!html) return { droppedLocalImages: 0, remoteImages: 0, tables: 0, paragraphs: 0 };
  return sanitize(html).report;
}

/**
 * `file:` and `cid:` cannot be fetched from an https page. `data:` is refused by the Image
 * extension (`allowBase64: false`, `031`) and would bloat the row. A foreign `blob:` belongs
 * to somebody else's document object and is dead the moment their tab closes.
 *
 * A broken `<img>` is worse than no image: it *looks* like the paste worked, and the user
 * finds the hole in their notes a week later. Removal is honest (§4.4).
 */
export function isFetchableImageSrc(src: string): boolean {
  const s = src.trim().toLowerCase();
  if (s === '') return false;
  if (s.startsWith('file:')) return false;
  if (s.startsWith('cid:')) return false;
  if (s.startsWith('data:')) return false;
  if (s.startsWith('blob:')) return false;
  return /^(?:https?:|\/)/.test(s);
}

/* ── The pipeline ───────────────────────────────────────────────────────────── */

function sanitize(html: string): { html: string; report: PasteReport } {
  const doc = new DOMParser().parseFromString(stripConditionalComments(html), 'text/html');
  const body = doc.body;

  const report: PasteReport = {
    droppedLocalImages: 0,
    remoteImages: 0,
    tables: 0,
    paragraphs: 0,
  };

  killElements(doc, body);
  removeComments(doc, body);
  dropUnfetchableImages(body, report);

  // Order matters from here to `scrubAttributes`: Word's list nesting lives in the `style`
  // attribute, and the scrub is what deletes `style`. Reconstruct before you scrub.
  unwrapAll(body, UNWRAP_TAGS);
  reconstructWordLists(doc, body);
  convertTaskLists(body);
  clampHeadings(doc, body);
  liftCaptions(doc, body);
  flattenNestedTables(doc, body);
  degradeSoup(doc, body);
  neutraliseFakeMarks(body);
  promoteStyleMarks(doc, body);

  for (const el of Array.from(body.querySelectorAll('*'))) scrubAttributes(el);

  unwrapBareSpans(body);
  removeEmptyInlines(body);
  normaliseNbspCells(body);

  countSurvivors(body, report);

  // Trimmed, and this is not cosmetic: it is what makes the function IDEMPOTENT
  // (`130-testing.md` §2.1 #15). A full clipboard document (`<html><head>…`) leaves leading
  // newlines in `body.innerHTML`, but re-parsing a bare fragment discards them in the
  // "before head" insertion mode — so an untrimmed output would not be a fixed point of
  // itself, and "sanitize twice" would not equal "sanitize once".
  return { html: body.innerHTML.trim(), report };
}

/**
 * Conditional comments, stripped BEFORE parsing — regex is correct here precisely because
 * this content is not part of the DOM we are about to build, and `DOMParser` would keep it
 * as comment nodes whose XML some browsers' error recovery lifts into the tree.
 *
 * **There are two forms and they must not be confused.** Getting this wrong is one of the
 * two ways to lose a whole table:
 *
 *  - *Downlevel-hidden* — `<!--[if gte mso 9]> … <![endif]-->`. The opener does NOT close
 *    the comment. In Word this wraps `<xml>` metadata and, for a table, **a complete second
 *    copy of the table**. Strip the block AND its contents; leave it and the table appears
 *    twice (paste twice and it is four times — `140-roadmap.md` M3 #10).
 *
 *  - *Downlevel-revealed* — `<!--[if !supportLists]--> … <!--[endif]-->` and
 *    `<![if !supportLists]> … <![endif]>`. The opener DOES close, so the content between the
 *    markers is **real markup that the browser rendered** — Word's list bullets live in
 *    exactly this construct. Strip the MARKERS ONLY and keep what is between them.
 *
 * The hidden pattern's opener is matched as `[^>]*?\]>`, which cannot match `]-->` (there is
 * a `-` between `]` and `>`). Without that precision the non-greedy match would run from a
 * `<!--[if !supportLists]-->` opener all the way to the next `<![endif]-->` far below and
 * delete every list item in between — a silent, catastrophic text loss.
 */
function stripConditionalComments(html: string): string {
  return (
    html
      // Downlevel-hidden: the block and everything inside it.
      .replace(/<!--\[if\b[^>]*?\]>(?:(?!<!\[endif\]-->)[\s\S])*?<!\[endif\]-->/gi, '')
      // Downlevel-revealed, comment form: markers only.
      .replace(/<!--\[if\b[^\]]*?\]-->|<!--\[endif\]-->/gi, '')
      // Downlevel-revealed, bare form: markers only.
      .replace(/<!\[if\b[^\]]*?\]>|<!\[endif\]>/gi, '')
      .replace(/<!--\s*(?:Start|End)Fragment\s*-->/gi, '')
      .replace(/<\?xml[\s\S]*?\?>/gi, '')
  );
}

function killElements(doc: Document, body: HTMLElement): void {
  for (const el of Array.from(body.querySelectorAll('*'))) {
    // Already detached by an ancestor's removal in this same loop.
    if (!body.contains(el)) continue;

    const name = el.nodeName.toUpperCase();

    // `<o:p>`, `<w:WordDocument>`, `<v:imagedata src="file:///…">`. Whole subtree.
    if (OFFICE_NS.test(el.nodeName) || HARD_KILL.has(name)) {
      el.remove();
      continue;
    }

    if (SALVAGE_KILL.has(name)) {
      const text = el.textContent?.trim() ?? '';
      if (text === '') {
        el.remove();
      } else {
        const p = doc.createElement('p');
        p.textContent = text;
        el.replaceWith(p);
      }
    }
  }
}

function removeComments(doc: Document, body: HTMLElement): void {
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_COMMENT);
  const comments: Comment[] = [];
  while (walker.nextNode()) comments.push(walker.currentNode as Comment);
  for (const comment of comments) comment.remove();
}

function dropUnfetchableImages(body: HTMLElement, report: PasteReport): void {
  for (const img of Array.from(body.querySelectorAll('img'))) {
    const src = img.getAttribute('src') ?? '';
    if (isFetchableImageSrc(src)) {
      if (/^https?:/i.test(src.trim())) report.remoteImages += 1;
      continue;
    }
    report.droppedLocalImages += 1;
    img.remove(); // THE ELEMENT, not just the attribute (§4.4).
  }
}

function unwrapAll(body: HTMLElement, selector: string): void {
  for (const el of Array.from(body.querySelectorAll(selector))) unwrap(el);
}

/**
 * A rendered Markdown task list (GitHub, and most static-site renderers) is
 * `<ul><li><input type=checkbox checked> text</li></ul>`. Our `taskItem` parses
 * `li[data-type="taskItem"]` and reads `data-checked` — nothing else. Left alone, the schema
 * drops the `<input>` and the item lands as a plain bullet with its checked state lost.
 * Rewriting it here is a structural mapping into the schema, not a Word workaround.
 */
function convertTaskLists(body: HTMLElement): void {
  for (const input of Array.from(body.querySelectorAll('li > input[type="checkbox"]'))) {
    const li = input.parentElement;
    if (li === null) continue;

    const checked =
      input.hasAttribute('checked') || (input as HTMLInputElement).checked ? 'true' : 'false';

    li.setAttribute('data-type', 'taskItem');
    li.setAttribute('data-checked', checked);
    input.remove();

    const list = li.parentElement;
    if (list !== null && list.nodeName.toUpperCase() === 'UL') {
      list.setAttribute('data-type', 'taskList');
    }
  }
}

/** `<h4>`–`<h6>` → `<h3>`. `031` allows levels 1–3; the text must not be lost to that. */
function clampHeadings(doc: Document, body: HTMLElement): void {
  for (const el of Array.from(body.querySelectorAll('h4, h5, h6'))) rename(doc, el, 'h3');
}

/** A table `<caption>` becomes a paragraph immediately ABOVE the table (§4.5). */
function liftCaptions(doc: Document, body: HTMLElement): void {
  for (const caption of Array.from(body.querySelectorAll('caption'))) {
    const table = caption.closest('table');
    const text = caption.textContent?.trim() ?? '';
    caption.remove();
    if (table === null || text === '') continue;
    const p = doc.createElement('p');
    p.textContent = text;
    table.parentNode?.insertBefore(p, table);
  }
}

/**
 * Our schema forbids `table` inside `tableCell`. ProseMirror's default for a node it cannot
 * place is to drop it **and its content** — so without this the inner table's text vanishes
 * silently. Flatten it to paragraphs instead: ugly beats missing (§4.5).
 *
 * Deepest-first, by looping until none remain: a table nested two deep flattens too.
 */
function flattenNestedTables(doc: Document, body: HTMLElement): void {
  for (let guard = 0; guard < 10; guard += 1) {
    const inner = body.querySelector('td table, th table');
    if (inner === null) return;

    const paragraphs = Array.from(inner.querySelectorAll('tr'))
      .map((tr) =>
        Array.from(tr.querySelectorAll('td, th'))
          .map((cell) => cell.textContent?.replace(/ /g, ' ').trim() ?? '')
          .filter(Boolean)
          .join(' — '),
      )
      .filter(Boolean)
      .map((line) => {
        const p = doc.createElement('p');
        p.textContent = line;
        return p;
      });

    inner.replaceWith(...paragraphs);
  }
}

/**
 * `<div>` soup and friends. A container holding block children is pure nesting → unwrap it.
 * One holding only inline content is a paragraph in all but name → rename it (§4.5).
 */
function degradeSoup(doc: Document, body: HTMLElement): void {
  for (const el of Array.from(body.querySelectorAll(SOUP_TAGS))) {
    if (!body.contains(el)) continue;

    const hasBlockChild = Array.from(el.children).some((child) =>
      BLOCK_TAGS.has(child.nodeName.toUpperCase()),
    );
    if (hasBlockChild) unwrap(el);
    else rename(doc, el, 'p');
  }
}

/* ── Marks: the two halves of the same problem (`130-testing.md` §2.1 #14) ──── */

/**
 * **Google Docs and Sheets wrap the entire clipboard payload in
 * `<b style="font-weight:normal">`.** It is a styling hook, not emphasis. ProseMirror's Bold
 * rule knows this and checks `style.fontWeight !== 'normal'` — but by the time it looks, we
 * have deleted every `style` attribute, and a bare `<b>` around the payload makes **the whole
 * paste bold**. Undoing the scrub's own blind spot is this function's entire job. It is the
 * reason a Sheets paste does not arrive shouting.
 */
function neutraliseFakeMarks(body: HTMLElement): void {
  for (const el of Array.from(body.querySelectorAll('b, strong, i, em'))) {
    if (!body.contains(el)) continue;
    const style = el.getAttribute('style') ?? '';
    const name = el.nodeName.toUpperCase();

    const fakeBold =
      (name === 'B' || name === 'STRONG') && /font-weight\s*:\s*(?:normal|400)\b/i.test(style);
    const fakeItalic = (name === 'I' || name === 'EM') && /font-style\s*:\s*normal\b/i.test(style);

    if (fakeBold || fakeItalic) unwrap(el);
  }
}

/** Elements whose emphasis is already structural, or whose boldness is the browser's. */
const NO_PROMOTE: ReadonlySet<string> = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'DEL',
  'TH',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
]);

/**
 * The other half: Google (and Word, sometimes) express emphasis as an inline **style**, not a
 * tag — `<span style="font-weight:700">`. Dropping `style` would drop the emphasis with it.
 * So the styles that DO map to one of `031`'s six marks are promoted into real tags first,
 * and everything else about `style` still dies. Colour, font and size are not "lost" — `031`
 * has no mark for them, so they were never representable.
 */
function promoteStyleMarks(doc: Document, body: HTMLElement): void {
  for (const el of Array.from(body.querySelectorAll('[style]'))) {
    if (!body.contains(el)) continue;
    if (NO_PROMOTE.has(el.nodeName.toUpperCase())) continue;
    if (el.firstChild === null) continue;

    const style = el.getAttribute('style') ?? '';
    const decoration = /text-decoration[^:]*:\s*([^;]*)/i.exec(style)?.[1] ?? '';

    const tags: string[] = [];
    if (/font-weight\s*:\s*(?:bold(?:er)?|[6-9]00)\b/i.test(style)) tags.push('strong');
    if (/font-style\s*:\s*italic\b/i.test(style)) tags.push('em');
    if (/underline/i.test(decoration)) tags.push('u');
    if (/line-through/i.test(decoration)) tags.push('s');
    if (tags.length === 0) continue;

    let inner: Element = el;
    for (const tag of tags) {
      const wrapper = doc.createElement(tag);
      while (inner.firstChild) wrapper.appendChild(inner.firstChild);
      inner.appendChild(wrapper);
      inner = wrapper;
    }
  }
}

/**
 * The allowlist. Every attribute not explicitly named is removed — which is what kills
 * every `mso-*` declaration (they live in `style`) and every `Mso*` class, without this
 * file ever containing the string `mso-`.
 */
function scrubAttributes(el: Element): void {
  const allowed = ALLOWED_ATTRS[el.nodeName.toUpperCase()] ?? [];

  for (const name of el.getAttributeNames()) {
    if (!allowed.includes(name.toLowerCase())) el.removeAttribute(name);
  }

  if (el.nodeName.toUpperCase() === 'A') {
    // No `javascript:`, no `data:`. The schema would not build a link mark from them
    // anyway, but do not lean on a second line of defence for something this cheap.
    const href = el.getAttribute('href') ?? '';
    if (!/^(?:https?:|mailto:|\/|#)/i.test(href.trim())) el.removeAttribute('href');
  }
}

/** Word nests `<span>` 3–5 deep. One pass peels one layer, so repeat until stable. */
function unwrapBareSpans(body: HTMLElement): void {
  for (let guard = 0; guard < 12; guard += 1) {
    const spans = Array.from(body.querySelectorAll('span')).filter(
      (span) => span.attributes.length === 0,
    );
    if (spans.length === 0) return;
    for (const span of spans) unwrap(span);
  }
}

/**
 * Elements that are now genuinely empty and cannot be meaningfully empty.
 *
 * **Table cells are EXEMPT and are not in the selector.** An empty `<td>` is a real,
 * load-bearing cell; removing it shifts every subsequent cell one column left and silently
 * corrupts the table. This omission is the single most important line in this file.
 */
function removeEmptyInlines(body: HTMLElement): void {
  for (const el of Array.from(body.querySelectorAll('span, b, i, u, strong, em, a, s, del'))) {
    if (!body.contains(el)) continue;
    if (isEffectivelyEmpty(el)) el.remove();
  }
}

/** Word's `&nbsp;`-only cells look empty, aren't, and defeat every "is it empty" check. */
function normaliseNbspCells(body: HTMLElement): void {
  for (const cell of Array.from(body.querySelectorAll('td, th'))) {
    const text = cell.textContent?.replace(/ /g, '').trim() ?? '';
    if (text === '' && cell.querySelector('img, table') === null) cell.textContent = '';
  }
}

function countSurvivors(body: HTMLElement, report: PasteReport): void {
  report.tables = body.querySelectorAll('table').length;
  report.paragraphs = body.querySelectorAll('p, h1, h2, h3, li, blockquote, pre').length;
}

/* ── Word's pseudo-lists (§4.5, `130-testing.md` §2.1 #10) ──────────────────── */

/**
 * **A deliberate, documented divergence from `061-editor.md` §4.5.**
 *
 * §4.5 says: leave Word's list paragraphs as paragraphs, bullet character and all, because
 * "the heuristics are notoriously fragile (they misfire on legitimate text starting with a
 * digit)". `130-testing.md` §2.1 #10 and `140-roadmap.md` M3 acceptance #8 say the opposite,
 * and in the same words as the requirement itself: a 3-level Word list must become "a real
 * nested `bulletList`, not eight flat paragraphs". Two documents to one, and the roadmap's
 * acceptance criterion is the checkable one — so we reconstruct.
 *
 * The reconciliation is in *what we key off*. §4.5's fear is a **content** heuristic: looking
 * at the text and guessing. We never look at the text. We key exclusively off `mso-list:`,
 * a machine-written marker that Word puts on a paragraph if and only if it IS a list item,
 * carrying the list id and the nesting level explicitly (`mso-list:l0 level2 lfo1`). A
 * paragraph without that marker is left alone, even if it starts with `1.` and even if it
 * carries `class=MsoListParagraph` (Word uses that class for plain indented paragraphs too).
 * There is no guessing anywhere in here, and therefore nothing to misfire.
 */
interface WordListItem {
  level: number;
  ordered: boolean;
  el: Element;
}

/** Word writes the rendered bullet or number into a `mso-list:Ignore` span. It is not the
 *  user's text — it is Word's rendering of a list marker — so it is removed, not kept. */
function takeListMarker(el: Element): Element | null {
  for (const span of Array.from(el.querySelectorAll('span'))) {
    const style = span.getAttribute('style') ?? '';
    if (/mso-list\s*:\s*ignore/i.test(style)) return span;
  }
  return null;
}

function readListItem(el: Element): WordListItem | null {
  if (el.nodeName.toUpperCase() !== 'P') return null;

  const style = el.getAttribute('style') ?? '';
  const marker = takeListMarker(el);

  // The ONLY trigger. No class heuristic, no text heuristic.
  const listed = /mso-list\s*:\s*l\d+/i.test(style) || marker !== null;
  if (!listed) return null;

  const level = Number.parseInt(/level(\d+)/i.exec(style)?.[1] ?? '1', 10);

  // Ordered vs bulleted comes from the glyph Word itself rendered: `1.`, `a)`, `iv.` are
  // ordered; `·`, `o`, `§`, `-` are bullets. This reads Word's own output, not the user's.
  const glyph = (marker?.textContent ?? '').replace(/ /g, ' ').trim();
  const ordered = /^(?:\d+|[a-z]{1,4}|[ivxlcdm]{1,6})[.)]/i.test(glyph);

  marker?.remove();

  return { level: Number.isFinite(level) && level > 0 ? level : 1, ordered, el };
}

function reconstructWordLists(doc: Document, body: HTMLElement): void {
  const parents: Element[] = [body, ...Array.from(body.querySelectorAll('*'))];

  for (const parent of parents) {
    if (!body.contains(parent) && parent !== body) continue;

    const children = Array.from(parent.children);
    let run: WordListItem[] = [];
    let anchor: Element | null = null;

    const flush = (): void => {
      if (run.length === 0) return;
      const lists = buildLists(doc, run);
      if (anchor !== null) for (const list of lists) parent.insertBefore(list, anchor);
      else for (const list of lists) parent.appendChild(list);
      for (const item of run) item.el.remove();
      run = [];
    };

    for (const child of children) {
      const item = readListItem(child);
      if (item === null) {
        flush();
        continue;
      }
      if (run.length === 0) anchor = child;
      run.push(item);
    }
    flush();
  }
}

function buildLists(doc: Document, items: WordListItem[]): Element[] {
  const roots: Element[] = [];
  const stack: { level: number; list: Element }[] = [];

  const tagFor = (ordered: boolean): string => (ordered ? 'ol' : 'ul');

  const openList = (item: WordListItem, parentEntry: { list: Element } | undefined): Element => {
    const list = doc.createElement(tagFor(item.ordered));
    if (parentEntry === undefined) {
      roots.push(list);
    } else {
      // A nested list belongs INSIDE the previous `<li>` — that is what makes it nested
      // rather than a sibling. `listItem` content is `paragraph block*`, so a `<ul>` after
      // the item's paragraph is exactly what the schema wants.
      const host = parentEntry.list.lastElementChild ?? parentEntry.list;
      host.appendChild(list);
    }
    return list;
  };

  for (const item of items) {
    while (stack.length > 0 && (stack.at(-1)?.level ?? 0) > item.level) stack.pop();

    let top = stack.at(-1);

    if (top === undefined || top.level < item.level) {
      const list = openList(item, top);
      stack.push({ level: item.level, list });
    } else if (
      top.level === item.level &&
      top.list.nodeName.toLowerCase() !== tagFor(item.ordered)
    ) {
      // A bulleted run followed by a numbered run at the same level: a sibling list, not a
      // continuation of the wrong type.
      stack.pop();
      const list = openList(item, stack.at(-1));
      stack.push({ level: item.level, list });
    }

    top = stack.at(-1);
    if (top === undefined) continue;

    const li = doc.createElement('li');
    const p = doc.createElement('p');
    while (item.el.firstChild) p.appendChild(item.el.firstChild);
    li.appendChild(p);
    top.list.appendChild(li);
  }

  return roots;
}

/* ── Small pure helpers ─────────────────────────────────────────────────────── */

function unwrap(el: Element): void {
  const parent = el.parentNode;
  if (parent === null) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

function rename(doc: Document, el: Element, tag: string): Element {
  const replacement = doc.createElement(tag);
  while (el.firstChild) replacement.appendChild(el.firstChild);
  el.replaceWith(replacement);
  return replacement;
}

function isEffectivelyEmpty(el: Element): boolean {
  if (el.querySelector('img, table, br, input') !== null) return false;
  return (el.textContent ?? '').replace(/[\s ]/g, '') === '';
}
